import { Check, CircleCheck } from 'lucide-react';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { Heading } from 'components/Heading';
import { joinPath, usePathContext } from 'engine/pathContext';
import { PathProvider } from 'components/PathProvider';
import { TextField } from 'components/FormComponents';
import { useEffect, useState, type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ResetPasswordRecordSchema, type ResetPasswordRecord } from 'shared/modelTypes/user';
import { Button } from 'components/Button';

export const Route = createFileRoute('/confirm-registration')({
	component: RouteComponent,
});

function RouteComponent() {
	const [status, setStatus] = useState<'default' | 'success' | 'error'>('default');
	const [error, setError] = useState<string | null>(null);

	const location = useLocation();
	const searchParams = new URLSearchParams(location.searchStr);
	const code = searchParams.get('code');
	const email = searchParams.get('email');

	useEffect(() => {
		const validateResetToken = async () => {
			try {
				const res = await fetch(`${import.meta.env.VITE_WEBSOCKET_URL}/validate-reset-token?code=${code}&email=${email}`);
				if (res.ok) {
					setStatus('default');
				} else {
					setStatus('error');
					const errorData = await res.json();
					setError(errorData.error);
				}
			} catch (err) {
				console.error('errors', err);
				setStatus('error');
				setError('An error occurred. Please try again later.');
			}
		};

		if (typeof code === 'string' && typeof email === 'string') {
			validateResetToken();
		} else {
			setStatus('error');
			setError('invalid token');
		}
	}, [code, email]);

	const methods = useForm<ResetPasswordRecord>({
		resolver: zodResolver(ResetPasswordRecordSchema),
	});

	const [passwordValid, setPasswordValid] = useState({
		length: false,
		uppercase: false,
		lowercase: false,
		number: false,
		specialChar: false,
	});

	const { watch } = methods;
	const formPath = usePathContext('');

	const password = watch(joinPath(formPath, 'password') as keyof ResetPasswordRecord);

	useEffect(() => {
		if (password !== undefined) {
			setPasswordValid({
				length: password.length >= 8,
				uppercase: /[A-Z]/.test(password),
				lowercase: /[a-z]/.test(password),
				number: /\d/.test(password),
				specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
			});
		}
	}, [password]);

	const getIconColor = (isValid: boolean) => (isValid ? 'text-green-500' : 'text-gray-500');

	const onSubmit = async (data: ResetPasswordRecord) => {
		try {
			const res = await fetch(`${import.meta.env.VITE_WEBSOCKET_URL}/reset-password`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ ...data, code, email }),
			});
			if (res.ok) {
				setStatus('success');
			} else {
				setStatus('error');
			}
		} catch (err) {
			console.error('errors', err);
			setStatus('error');
		}
	};

	if (status === 'success') {
		return (
			<Layout>
				<div className="text-center">
					<div className="relative mb-6 inline-flex items-center justify-center">
						<span className="absolute inline-flex size-14 animate-ping rounded-full bg-green-600 opacity-75"></span>

						<span className="relative inline-flex size-14 items-center justify-center rounded-full bg-green-600">
							<Check className="size-6 text-white" />
						</span>
					</div>
					<Heading>Registration confirmed!</Heading>
					<p>Your account has been confirmed successfully. Click below to log in.</p>
					<Button size="lg" className="mt-6 w-full" to="/login">
						Login
					</Button>
				</div>
			</Layout>
		);
	}

	if (status === 'error') {
		let head = '';
		let body = '';

		switch (error) {
			case 'invalid data':
				head = 'Oops, something went wrong';
				body = 'The data provided is invalid or incomplete. Please check the link or contact your account administrator.';
				break;
			case 'token not found':
				head = 'Oops, token not found';
				body =
					"We couldn't find a valid confirmation token. It may have been deleted or never existed. Please contact your account administrator.";
				break;
			case 'user not found':
				head = 'User not found';
				body = "We couldn't match this link to any new user. Please contact your account administrator.";
				break;
			case 'invalid token':
				head = 'Invalid reset link';
				body =
					'This reset token is not valid. It might have been modified or is no longer recognized. Please contact your account administrator.';
				break;
			default:
				head = 'Confirmation failed';
				body = 'Please try again or contact your account administrator.';
				break;
		}

		return (
			<Layout>
				<div className="text-center">
					<Heading>{head}</Heading>
					<p>{body}</p>
					{/* <Button className="mt-6 w-full" to="/forgot-password">
						Try again
					</Button> */}
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<FormProvider {...methods}>
				<form className="flex h-auto w-full grow flex-col gap-3" onSubmit={methods.handleSubmit(onSubmit)}>
					<PathProvider path="">
						<Heading mode="secondary">Confirm registration</Heading>
						<p>Your email address has been successfully verified. You can now set your password and log in below.</p>
						<TextField name="password" label="New password" type="password" placeholder="Enter new password" />
						<TextField name="passwordConfirm" label="Confirm new password" type="password" placeholder="Confirm new password" />
						<div className="flex flex-col gap-2">
							{[
								{
									validation: [passwordValid.length],
									text: 'Minimum 8 characters',
								},
								{
									validation: [passwordValid.uppercase, passwordValid.lowercase],
									text: 'Must contain at least 1 uppercase letter and 1 lowercase letter',
								},
								{
									validation: [passwordValid.number],
									text: 'Must contain at least 1 digit',
								},
								{
									validation: [passwordValid.specialChar],
									text: 'Must contain at least 1 special character (e.g. @$%^)',
								},
							].map(({ validation, text }, index) => {
								return (
									<div key={index} className="flex flex-row items-center gap-2">
										<CircleCheck
											className={`flex size-5 min-w-5 text-gray-500 transition-all duration-1000 ease-in-out ${getIconColor(validation.every(Boolean))}`}
										/>
										<span className="font-xs weight-400 flex text-xs text-gray-500">{text}</span>
									</div>
								);
							})}
						</div>
					</PathProvider>
					<div className="absolute bottom-0 left-0 flex w-full flex-col gap-3 p-5 md:static md:bottom-auto md:left-auto md:w-auto md:p-0">
						<Button mode="tertiary" className="mt-6 w-full bg-black" type="submit">
							Submit password
						</Button>
						{/* <Button mode="transparent" className="w-full" to="/login">
							<ChevronLeft />
							Back to login
						</Button> */}
					</div>
				</form>
			</FormProvider>
		</Layout>
	);
}

interface LayoutProps {
	children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
	return (
		<div className="flex h-screen flex-row">
			<div className="mx-auto flex w-full flex-col justify-center gap-6 p-5 md:my-auto md:w-[424px] md:p-10">{children}</div>
		</div>
	);
};
